import type { Case } from "@auction-eth/shared-types";

/**
 * Normalised haystack for Case Queue search across institution / NOC:
 * case ID, borrower, loan refs, asset naming fields.
 */
export function buildCaseSearchHaystack(c: Case): string {
  const parts: string[] = [c.case_id, c.linked_loan_id, c.borrower_id];
  const fast = c.fast_snapshot;
  if (fast) {
    parts.push(fast.borrower_info?.full_name);
    parts.push(fast.loan_detail?.loan_ref);
    parts.push(fast.asset_info?.asset_type);
    parts.push(fast.asset_info?.asset_category);
    if (fast.asset_info?.description) parts.push(fast.asset_info.description);
    if (fast.asset_info?.serial_number_or_plate) parts.push(fast.asset_info.serial_number_or_plate);
    if (fast.asset_info?.address) parts.push(fast.asset_info.address);
  }
  const auction = c.auction_info;
  if (auction?.product_name) parts.push(String(auction.product_name));
  if (auction?.product_summary) parts.push(String(auction.product_summary));

  const mp = c.module_payloads ?? {};
  for (const payload of Object.values(mp)) {
    if (!payload || typeof payload !== "object") continue;
    const row = payload as Record<string, unknown>;
    if (typeof row.asset_name === "string") parts.push(row.asset_name);
    if (typeof row.asset_description === "string") parts.push(row.asset_description);
    if (typeof row.loan_reference === "string") parts.push(row.loan_reference);
  }

  return parts
    .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    .join(" ")
    .toLowerCase();
}

/** Every whitespace-separated token must appear somewhere in the joined blobs (order-independent). */
export function caseQueueSearchTokensMatch(rawQuery: string, ...blobs: string[]): boolean {
  const tokens = rawQuery
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = blobs.map((b) => (b ?? "").toLowerCase()).join(" ");
  return tokens.every((t) => hay.includes(t));
}

/** Match a full case document (institution queue). */
export function caseMatchesSearchQuery(c: Case, rawQuery: string): boolean {
  return caseQueueSearchTokensMatch(rawQuery, buildCaseSearchHaystack(c));
}

/** Map case_id → haystack for NOC rows that only carry case_id (alerts, dispatch, health summaries). */
export function buildCaseHaystackMap(cases: Case[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const c of cases) {
    m.set(c.case_id, buildCaseSearchHaystack(c));
  }
  return m;
}

/**
 * Match a shallow row using enrichment from {@link buildCaseHaystackMap}.
 * Falls back to case_id substring match when the case was not in the list payload.
 */
export function caseIdRowMatchesSearchQuery(
  caseId: string,
  rawQuery: string,
  haystackByCaseId: ReadonlyMap<string, string>,
  ...extraChunks: string[]
): boolean {
  const base = haystackByCaseId.get(caseId) ?? caseId;
  return caseQueueSearchTokensMatch(rawQuery, base, ...extraChunks);
}
